import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useQuery } from "@tanstack/react-query";
import {
  Menu,
  X,
  Home,
  Trophy,
  Calendar,
  Users,
  Settings,
  LogOut,
  User,
  Shield,
  Gamepad2,
} from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import { getUserByEmail } from "../lib/api/users";
import { generateClient } from "aws-amplify/api";
import { listLeagues } from "../graphql/queries";
import { getCurrentLeague } from "../lib/leagueUtils";
import { getLeagueGame } from "../lib/leagueGames";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { signOut, user } = useAuthenticator((context) => [
    context.signOut,
    context.user,
  ]);
  const location = useLocation();
  const client = generateClient();

  // Custom logout function that properly clears stored tokens
  const handleLogout = async () => {
    try {
      console.log("Logging out user...");

      // Clear all stored tokens and user data
      sessionStorage.clear();
      localStorage.clear();

      // Specifically clear Amplify-related storage
      localStorage.removeItem("amplify-signin-with-hostedUI");
      localStorage.removeItem("amplify-signin-with-hostedUI-state");
      localStorage.removeItem("amplify-signin-with-hostedUI-codeVerifier");
      localStorage.removeItem("amplify-signin-with-hostedUI-pkce");

      // Clear any other potential storage keys
      Object.keys(sessionStorage).forEach((key) => {
        if (
          key.includes("amplify") ||
          key.includes("cognito") ||
          key.includes("auth")
        ) {
          sessionStorage.removeItem(key);
        }
      });

      Object.keys(localStorage).forEach((key) => {
        if (
          key.includes("amplify") ||
          key.includes("cognito") ||
          key.includes("auth")
        ) {
          localStorage.removeItem(key);
        }
      });

      // Try to call the Amplify signOut function
      try {
        await signOut();
      } catch (error) {
        console.log(
          "Amplify signOut failed, but continuing with manual logout:",
          error
        );
      }

      // Add a small delay to ensure storage is cleared
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Force a page reload to clear any cached state and redirect to home
      window.location.href = "/";
    } catch (error) {
      console.error("Error during logout:", error);
      // Even if there's an error, clear everything and redirect
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = "/";
    }
  };

  // Get current user data to check admin role and display user info
  const { data: userData } = useQuery({
    queryKey: ["user", "stored-email"],
    queryFn: async () => {
      // Get email from stored tokens since we're using manual OAuth
      let email = sessionStorage.getItem("email");

      // Fallback: try to get email from user object or stored tokens
      if (!email && user) {
        email = user.signInDetails?.loginId || "";
      }

      // Fallback: try to decode from stored token
      if (!email) {
        const idToken = sessionStorage.getItem("id_token");
        if (idToken) {
          try {
            const payload = JSON.parse(atob(idToken.split(".")[1]));
            email = payload.email || "";
            if (email) {
              sessionStorage.setItem("email", email);
            }
          } catch (error) {
            console.error("Layout: Error decoding stored token:", error);
          }
        }
      }

      if (email) {
        return await getUserByEmail(email);
      }
      return null;
    },
    enabled: !!(sessionStorage.getItem("email") || user),
  });

  // Get Google profile picture from stored token
  const getGoogleProfilePicture = (): string | null => {
    const idToken = sessionStorage.getItem("id_token");
    if (idToken) {
      try {
        const payload = JSON.parse(atob(idToken.split(".")[1]));
        return payload.picture || null;
      } catch (error) {
        console.error("Layout: Error decoding stored token:", error);
      }
    }
    return null;
  };

  // Fetch every league so date-based selection can choose the active season.
  const { data: leagues } = useQuery({
    queryKey: ["leagues"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listLeagues,
        variables: { limit: 100 },
      });
      return result.data.listLeagues.items;
    },
  });

  const isAdmin = userData?.role === "ADMIN";
  // Default navigation to the current Magic season, then fall back to the
  // most relevant active league of any game.
  const currentMagicLeague = leagues
    ? getCurrentLeague(
        (leagues as any).filter(
          (league: any) =>
            getLeagueGame(league.game) === "MAGIC_THE_GATHERING"
        )
      )
    : null;
  const currentLeague =
    currentMagicLeague || (leagues ? getCurrentLeague(leagues as any) : null);
  const defaultLeagueId = currentLeague?.id || leagues?.[0]?.id;

  const navigation = [
    { name: "Dashboard", href: "/dashboard", icon: Home },
    {
      name: "Standings",
      href: defaultLeagueId
        ? `/league/${defaultLeagueId}/standings`
        : "/dashboard",
      icon: Trophy,
    },
    {
      name: "Events",
      href: defaultLeagueId
        ? `/league/${defaultLeagueId}/events`
        : "/dashboard",
      icon: Calendar,
    },
    { name: "Players", href: "/players", icon: Users },
  ];

  const adminNavigation = [
    { name: "Admin Dashboard", href: "/admin", icon: Shield },
    { name: "League Management", href: "/admin/leagues", icon: Trophy },
    { name: "Create League", href: "/admin/leagues/create", icon: Trophy },
    { name: "Player Mapping", href: "/admin/player-mapping", icon: Users },
    { name: "Promote User", href: "/admin/promote", icon: Users },
    { name: "Create User", href: "/admin/create-user", icon: Users },
  ];

  const isActive = (href: string) => {
    // Exact match first
    if (location.pathname === href) {
      return true;
    }

    // For admin routes, use more specific matching to avoid multiple highlights
    if (href.startsWith("/admin")) {
      // Special handling for admin dashboard - only match exact path
      if (href === "/admin") {
        return (
          location.pathname === "/admin" || location.pathname === "/admin/"
        );
      }

      // Special handling for admin/leagues - should NOT match admin/leagues/create
      if (href === "/admin/leagues") {
        return (
          location.pathname.startsWith("/admin/leagues/") &&
          !location.pathname.startsWith("/admin/leagues/create")
        );
      }

      // For other admin routes, match if current path starts with href
      return location.pathname.startsWith(href + "/");
    }

    // For other routes, use the original logic
    return location.pathname.startsWith(href + "/");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar */}
      <div
        className={cn(
          "fixed inset-0 z-50 lg:hidden",
          sidebarOpen ? "block" : "hidden"
        )}
      >
        <div
          className="fixed inset-0 bg-black/20"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 w-72 border-r border-white/10 bg-card/95 shadow-2xl backdrop-blur-xl">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex-shrink-0">
                {getGoogleProfilePicture() ? (
                  <img
                    src={getGoogleProfilePicture()!}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
              </div>
              <div>
                <h1 className="text-lg font-semibold">League Hub</h1>
                <p className="text-xs text-muted-foreground">
                  {userData?.displayName || userData?.firstName || "User"}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <nav className="p-4 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center space-x-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive(item.href)
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent"
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
            {isAdmin && (
              <>
                <div className="pt-4 border-t">
                  <h3 className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Admin
                  </h3>
                </div>
                {adminNavigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        "flex items-center space-x-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                        isActive(item.href)
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent"
                      )}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
              </>
            )}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-grow flex-col border-r border-white/10 bg-card/70 backdrop-blur-xl">
          <div className="flex h-20 items-center border-b border-white/10 px-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary shadow-lg shadow-primary/25">
              <Gamepad2 className="h-5 w-5 text-white" />
            </div>
            <div className="ml-3">
              <h1 className="text-base font-bold tracking-tight">League Hub</h1>
              <p className="text-[11px] text-muted-foreground">
                THE BOOSTER BOX
              </p>
            </div>
          </div>
          <nav className="flex-1 space-y-2 p-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center space-x-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive(item.href)
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent"
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
            {isAdmin && (
              <>
                <div className="pt-4 border-t">
                  <h3 className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Admin
                  </h3>
                </div>
                {adminNavigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        "flex items-center space-x-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                        isActive(item.href)
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent"
                      )}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
              </>
            )}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top bar */}
        <div className="glass sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-x-0 border-t-0 px-4 shadow-lg shadow-black/5 sm:gap-x-6 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* User menu */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  {/* Profile Picture */}
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex-shrink-0">
                    {getGoogleProfilePicture() ? (
                      <img
                        src={getGoogleProfilePicture()!}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* User Display Name */}
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">
                      {userData?.displayName ||
                        userData?.firstName ||
                        user?.signInDetails?.loginId ||
                        "User"}
                    </span>
                    {userData?.displayName && userData?.firstName && (
                      <span className="text-xs text-muted-foreground">
                        {userData.firstName} {userData.lastName}
                      </span>
                    )}
                  </div>
                </div>
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  title="Profile settings"
                >
                  <Settings className="h-4 w-4" />
                  <span className="hidden sm:inline">Profile</span>
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  title="Sign out"
                >
                  <LogOut className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
